/**
 * Access Lens — a READ projection of one actor's *authorized* access surface
 * (specs/v3/screens/11-access-lens.html). It explains, for a given actor, the
 * effective authority the platform grants: identities, role paths, context
 * boundaries (memberships), action policies, delegated capabilities, credential
 * grants (scopes only — never secrets), and what is redacted.
 *
 * Lens/projection doctrine (mirrors DecisionRoom + RenderState): this screen
 * *renders* authorized state, it never grants policy, mints credentials, or
 * mutates business truth. The only server interaction is the authorized read
 * `GET /api/actors/:actorId/passport` (actors:show), which is the AccessGuard's
 * read model — secret references are stripped server-side and surfaced here only
 * as the `redacted` state, never as values.
 *
 * The page body is routed through the shared eight-state contract
 * (`RenderState`, S13): loading skeleton, empty (no authorized records),
 * normal (the populated workspace), stale (watermark), overflow (hidden count),
 * redacted (sealed/omitted fields disclosed non-leakily), forbidden (the read
 * itself is not permitted), and error (recoverable failure).
 */
import * as React from "react";
import { useParams } from "react-router-dom";

import type { z } from "zod";

import type { ActorPassportPacket } from "@aos/shared";
import {
  actorAuthorityCredentialGrantSchema,
  actorAuthorityDelegationGrantSchema,
  actorAuthorityMembershipSchema,
  actorAuthorityPolicySchema,
  actorAuthorityRolePathSchema,
} from "@aos/shared";

// The actor-authority row types are not re-exported by name from the barrel;
// derive them from the exported Zod schemas (single source of truth).
type ActorAuthorityCredentialGrant = z.infer<typeof actorAuthorityCredentialGrantSchema>;
type ActorAuthorityDelegationGrant = z.infer<typeof actorAuthorityDelegationGrantSchema>;
type ActorAuthorityMembership = z.infer<typeof actorAuthorityMembershipSchema>;
type ActorAuthorityPolicy = z.infer<typeof actorAuthorityPolicySchema>;
type ActorAuthorityRolePath = z.infer<typeof actorAuthorityRolePathSchema>;
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Icon, type IconName } from "@/components/ui/icon";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { StatusBadge } from "@/components/app/StatusBadge";
import { PageHeader } from "@/components/layout/PageHeader";
import { Field, FieldGrid, InspectorSection } from "@/components/inspector";
import {
  RenderState,
  type RenderStateKind,
} from "@/components/app/RenderState";
import { ApiError } from "@/lib/api";
import { api } from "@/lib/api";
import { formatDateTime, humanize } from "@/lib/format";

/* ----------------------------------------------------------------- packet io */

interface PassportResult {
  data: ActorPassportPacket | null;
  loading: boolean;
  error: unknown;
  /** HTTP status of the failed read (so the body can become `forbidden`). */
  status: number | null;
  reload: () => void;
}

function useActorPassport(actorId: string): PassportResult {
  const [data, setData] = React.useState<ActorPassportPacket | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<unknown>(null);
  const [status, setStatus] = React.useState<number | null>(null);
  const [reloadKey, setReloadKey] = React.useState(0);
  const reload = React.useCallback(() => setReloadKey((value) => value + 1), []);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setStatus(null);
    api
      .get<ActorPassportPacket>(`/actors/${encodeURIComponent(actorId)}/passport`)
      .then((packet) => {
        if (!cancelled) setData(packet);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err);
        setStatus(err instanceof ApiError ? err.status : null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [actorId, reloadKey]);

  return { data, loading, error, status, reload };
}

/* --------------------------------------------------------------------- lenses */

type Lens =
  | "actor"
  | "roles"
  | "boundaries"
  | "policies"
  | "approvals"
  | "credentials"
  | "redactions";

interface LensDef {
  readonly key: Lens;
  readonly label: string;
  readonly icon: IconName;
}

const LENSES: readonly LensDef[] = [
  { key: "actor", label: "Actor", icon: "user" },
  { key: "roles", label: "Roles", icon: "roles" },
  { key: "boundaries", label: "Boundaries", icon: "scope" },
  { key: "policies", label: "Policies", icon: "rules" },
  { key: "approvals", label: "Approvals", icon: "approve" },
  { key: "credentials", label: "Credentials", icon: "tool" },
  { key: "redactions", label: "Redactions", icon: "reject" },
];

/* -------------------------------------------------------------------- helpers */

function ActiveBadge({ active }: { active: boolean }) {
  return active ? (
    <Badge variant="outline">
      <Icon name="resultOk" aria-hidden />
      Active
    </Badge>
  ) : (
    <Badge variant="secondary">Inactive</Badge>
  );
}

function orDash(value: string | null | undefined): React.ReactNode {
  return value ? value : <span className="text-muted-foreground">—</span>;
}

function Metric({ label, value }: { label: string; value: number | string }) {
  return (
    <Card>
      <CardHeader className="p-3 pb-1">
        <CardTitle className="text-xs font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-0">
        <p className="font-mono text-sm tabular-nums">{value}</p>
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------- lens sections */

function ActorLens({ packet }: { packet: ActorPassportPacket }) {
  const { actor, runtime_principal: rp, summary } = packet;
  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Metric label="Active identities" value={summary.active_identities} />
        <Metric label="Active role paths" value={summary.active_role_paths} />
        <Metric label="Active delegations" value={summary.active_delegations} />
        <Metric label="Active credentials" value={summary.active_credential_grants} />
      </div>

      <InspectorSection
        title="Actor"
        aside={
          <span className="text-xs text-muted-foreground">
            Generated {formatDateTime(packet.generated_at)}
          </span>
        }
      >
        <FieldGrid>
          <Field label="Display name">{actor.display_name}</Field>
          <Field label="Actor">
            <span className="font-mono text-xs">{actor.id}</span>
          </Field>
          <Field label="Type">
            <Badge variant="outline">{humanize(actor.actor_type)}</Badge>
          </Field>
          <Field label="Class">
            <Badge variant="outline">{humanize(actor.actor_class)}</Badge>
          </Field>
          <Field label="Status">
            <StatusBadge status={actor.status} />
          </Field>
          <Field label="Surface">
            <StatusBadge status={packet.surface_state} />
          </Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Effective authority (runtime principal)">
        <FieldGrid>
          <Field label="Approval mode">
            <Badge variant="outline">{humanize(rp.approval_mode)}</Badge>
          </Field>
          <Field label="Approvers required">{rp.required_approver_count}</Field>
          <Field label="Distinct approver">
            {rp.requires_distinct_approver === 1 ? "Required" : "Not required"}
          </Field>
          <Field label="Authorized via">
            {rp.authorized_via ? humanize(rp.authorized_via.path_type) : orDash(null)}
          </Field>
          <Field label="Active roles" full>
            {rp.active_role_names.length === 0 ? (
              orDash(null)
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {rp.active_role_names.map((name) => (
                  <Badge key={name} variant="outline">
                    {humanize(name)}
                  </Badge>
                ))}
              </div>
            )}
          </Field>
          <Field label="Active capabilities" full>
            {rp.active_capabilities.length === 0 ? (
              orDash(null)
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {rp.active_capabilities.map((cap) => (
                  <Badge key={cap} variant="secondary" className="font-mono">
                    {cap}
                  </Badge>
                ))}
              </div>
            )}
          </Field>
          <Field label="Fingerprint" full>
            <span className="font-mono text-xs [overflow-wrap:anywhere]">
              {rp.principal_fingerprint}
            </span>
          </Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Identities">
        {packet.identities.length === 0 ? (
          <EmptyLens
            title="No identities."
            hint="This actor has no linked sign-in identity yet."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Provider</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Last seen</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {packet.identities.map((identity) => (
                <TableRow key={identity.id}>
                  <TableCell className="font-mono text-xs">{identity.provider}</TableCell>
                  <TableCell>{orDash(identity.email)}</TableCell>
                  <TableCell>{formatDateTime(identity.last_seen_at)}</TableCell>
                  <TableCell>
                    <ActiveBadge active={identity.active_now} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </InspectorSection>
    </div>
  );
}

function RolesLens({ rolePaths }: { rolePaths: readonly ActorAuthorityRolePath[] }) {
  if (rolePaths.length === 0) {
    return (
      <EmptyLens
        title="No role grants."
        hint="This actor holds no direct or membership-inherited role assignments."
      />
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Role</TableHead>
          <TableHead>Path</TableHead>
          <TableHead>From</TableHead>
          <TableHead>Context</TableHead>
          <TableHead>Status</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rolePaths.map((path) => (
          <TableRow key={path.id}>
            <TableCell>
              <Badge variant="outline">{humanize(path.role.name)}</Badge>
            </TableCell>
            <TableCell className="text-xs text-muted-foreground">
              {humanize(path.path_type)}
            </TableCell>
            <TableCell>
              {path.path_type === "membership_role_assignment"
                ? orDash(path.source_actor_display_name ?? path.source_actor_id)
                : "Direct"}
            </TableCell>
            <TableCell className="font-mono text-xs">{orDash(path.context_id)}</TableCell>
            <TableCell>
              <ActiveBadge active={path.active_now} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function BoundariesLens({
  memberships,
}: {
  memberships: readonly ActorAuthorityMembership[];
}) {
  if (memberships.length === 0) {
    return (
      <EmptyLens
        title="No context boundaries."
        hint="This actor belongs to no collective or context that scopes its authority."
      />
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Parent</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Context</TableHead>
          <TableHead>Expires</TableHead>
          <TableHead>Status</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {memberships.map((membership) => (
          <TableRow key={membership.id}>
            <TableCell>
              {orDash(membership.parent_actor_display_name ?? membership.parent_actor_id)}
            </TableCell>
            <TableCell className="text-xs text-muted-foreground">
              {humanize(membership.membership_type)}
            </TableCell>
            <TableCell className="font-mono text-xs">{orDash(membership.context_id)}</TableCell>
            <TableCell>{formatDateTime(membership.expires_at)}</TableCell>
            <TableCell>
              <ActiveBadge active={membership.active_now} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function PoliciesLens({ policies }: { policies: readonly ActorAuthorityPolicy[] }) {
  if (policies.length === 0) {
    return (
      <EmptyLens
        title="No action policies in effect."
        hint="No risk/approval policy overrides scope this actor's capabilities."
      />
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Capability</TableHead>
          <TableHead>Risk</TableHead>
          <TableHead>Approval mode</TableHead>
          <TableHead>Approvers</TableHead>
          <TableHead>Context</TableHead>
          <TableHead>Status</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {policies.map((policy) => (
          <TableRow key={policy.id}>
            <TableCell className="font-mono text-xs">{policy.capability}</TableCell>
            <TableCell>
              <Badge variant="secondary">{humanize(policy.risk_level)}</Badge>
            </TableCell>
            <TableCell>
              <Badge variant="outline">{humanize(policy.approval_mode)}</Badge>
            </TableCell>
            <TableCell className="font-mono text-xs tabular-nums">
              {policy.required_approver_count}
              {policy.requires_distinct_approver === 1 ? " (distinct)" : ""}
            </TableCell>
            <TableCell className="font-mono text-xs">{orDash(policy.context_id)}</TableCell>
            <TableCell>
              <StatusBadge status={policy.status} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function ApprovalsLens({
  delegations,
}: {
  delegations: readonly ActorAuthorityDelegationGrant[];
}) {
  if (delegations.length === 0) {
    return (
      <EmptyLens
        title="No delegated authority."
        hint="No actor has delegated a capability to this actor (delegations carry an approval ceiling)."
      />
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Capability</TableHead>
          <TableHead>From</TableHead>
          <TableHead>Context</TableHead>
          <TableHead>Ceiling policy</TableHead>
          <TableHead>Expires</TableHead>
          <TableHead>Status</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {delegations.map((grant) => (
          <TableRow key={grant.id}>
            <TableCell className="font-mono text-xs">{grant.capability}</TableCell>
            <TableCell>
              {orDash(grant.delegator_display_name ?? grant.delegator_actor_id)}
            </TableCell>
            <TableCell className="font-mono text-xs">{orDash(grant.context_id)}</TableCell>
            <TableCell className="font-mono text-xs">{orDash(grant.ceiling_policy_id)}</TableCell>
            <TableCell>{formatDateTime(grant.expires_at)}</TableCell>
            <TableCell>
              <ActiveBadge active={grant.active_now} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function CredentialsLens({
  grants,
}: {
  grants: readonly ActorAuthorityCredentialGrant[];
}) {
  if (grants.length === 0) {
    return (
      <EmptyLens
        title="No credential grants."
        hint="This actor can invoke no tool credentials. Even when grants exist, secret values and secret references are never shown here — only scopes."
      />
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-muted-foreground">
        ToolBroker credential boundary: scopes are shown, secret values and secret
        references are never included.
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Tool</TableHead>
            <TableHead>Capability</TableHead>
            <TableHead>Scopes</TableHead>
            <TableHead>Context</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {grants.map((grant) => (
            <TableRow key={grant.id}>
              <TableCell className="font-mono text-xs">{grant.tool_name}</TableCell>
              <TableCell className="font-mono text-xs">{grant.capability}</TableCell>
              <TableCell>
                {grant.allowed_scopes.length === 0 ? (
                  orDash(null)
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {grant.allowed_scopes.map((scope) => (
                      <Badge key={scope} variant="secondary" className="font-mono">
                        {scope}
                      </Badge>
                    ))}
                  </div>
                )}
              </TableCell>
              <TableCell className="font-mono text-xs">{orDash(grant.context_id)}</TableCell>
              <TableCell>
                <ActiveBadge active={grant.active_now} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function RedactionsLens({ packet }: { packet: ActorPassportPacket }) {
  const warnings = packet.integrity_warnings;
  return (
    <div className="flex flex-col gap-6">
      <InspectorSection title="Redaction boundary">
        <FieldGrid>
          <Field label="Credential secrets">
            <Badge variant="secondary">
              <Icon name="reject" aria-hidden />
              Withheld by policy
            </Badge>
          </Field>
          <Field label="Secret references">
            <Badge variant="secondary">
              <Icon name="reject" aria-hidden />
              Withheld by policy
            </Badge>
          </Field>
        </FieldGrid>
        <p className="mt-3 text-sm text-muted-foreground">
          The actor passport is a sanitized read model. Credential secret values
          and secret references are stripped server-side and are never present in
          this projection — only scopes and grant lineage are visible.
        </p>
      </InspectorSection>

      <InspectorSection title="Integrity warnings">
        {warnings.length === 0 ? (
          <EmptyLens
            title="No integrity warnings."
            hint="The effective authority is internally consistent for this request."
          />
        ) : (
          <ul className="flex flex-col gap-2">
            {warnings.map((warning) => (
              <li
                key={warning}
                className="flex items-start gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm"
              >
                <Icon name="reject" className="mt-0.5 shrink-0 text-muted-foreground" aria-hidden />
                <span className="[overflow-wrap:anywhere]">{warning}</span>
              </li>
            ))}
          </ul>
        )}
      </InspectorSection>
    </div>
  );
}

/** A lens-local empty notice (the eight-state `empty` for an individual lens). */
function EmptyLens({ title, hint }: { title: string; hint: string }) {
  return (
    <RenderState state="empty" empty={{ icon: "scope", title, hint }} />
  );
}

/* ------------------------------------------------------------- workspace body */

function LensWorkspace({ packet, lens }: { packet: ActorPassportPacket; lens: Lens }) {
  switch (lens) {
    case "actor":
      return <ActorLens packet={packet} />;
    case "roles":
      return <RolesLens rolePaths={packet.role_paths} />;
    case "boundaries":
      return <BoundariesLens memberships={packet.memberships} />;
    case "policies":
      return <PoliciesLens policies={packet.policy_overrides} />;
    case "approvals":
      return <ApprovalsLens delegations={packet.delegation_grants} />;
    case "credentials":
      return <CredentialsLens grants={packet.credential_grants} />;
    case "redactions":
      return <RedactionsLens packet={packet} />;
    default:
      return null;
  }
}

/* -------------------------------------------------------------------- routes */

export function AccessLensRoute() {
  const params = useParams<{ id?: string; actorId?: string }>();
  const actorId = params.actorId ?? params.id ?? null;

  if (!actorId) {
    return (
      <RenderState
        state="empty"
        empty={{
          icon: "user",
          title: "Choose an actor to explain.",
          hint: "Access Lens explains the authorized access surface for one actor.",
        }}
      />
    );
  }

  return <AccessLensPage actorId={actorId} />;
}

function AccessLensPage({ actorId }: { actorId: string }) {
  const { data, loading, error, status, reload } = useActorPassport(actorId);
  const [lens, setLens] = React.useState<Lens>("actor");

  // Map the read result onto the eight-state contract. A 403 on the read itself
  // is `forbidden` (the lens is not permitted); 404 / others fall through to a
  // recoverable `error`. The redacted state is implicit in the credential lens.
  const bodyState: RenderStateKind = loading
    ? "loading"
    : error
      ? status === 403
        ? "forbidden"
        : "error"
      : data
        ? "normal"
        : "empty";

  return (
    <div className="max-w-6xl">
      <div className="flex flex-col gap-6">
        <PageHeader
          title="Access Lens"
          subtitle="Authorized read projection: actor, role paths, context boundaries, action policies, delegated authority, credential scopes, and redactions."
          icon="roles"
          actions={
            <div className="flex items-center gap-2">
              <Select value={lens} onValueChange={(value) => setLens(value as Lens)}>
                <SelectTrigger className="w-[160px]" aria-label="View lens">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LENSES.map((def) => (
                    <SelectItem key={def.key} value={def.key}>
                      {def.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={reload}>
                <Icon name="reopen" data-icon="inline-start" aria-hidden />
                Refresh
              </Button>
            </div>
          }
        />

        {/* Lens tab strip mirrors the mockup's segment strip; each tab selects a
            projection of the same authorized read. */}
        <div
          className="flex flex-wrap gap-1.5 border-b border-border pb-3"
          role="tablist"
          aria-label="Access lenses"
        >
          {LENSES.map((def) => {
            const active = def.key === lens;
            return (
              <Button
                key={def.key}
                type="button"
                role="tab"
                aria-selected={active}
                variant={active ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setLens(def.key)}
              >
                <Icon name={def.icon} data-icon="inline-start" aria-hidden />
                {def.label}
              </Button>
            );
          })}
        </div>

        <RenderState
          state={bodyState}
          loadingRows={6}
          error={error}
          onRetry={reload}
          forbiddenReason="You are not permitted to view this actor's access surface."
          forbiddenApprovalPath="an administrator (actors:show)"
          empty={{
            icon: "user",
            title: "No authorized access records for this actor.",
            hint: "The actor exists but exposes no authority on this projection.",
          }}
        >
          {data ? <LensWorkspace packet={data} lens={lens} /> : null}
        </RenderState>
      </div>
    </div>
  );
}

export default AccessLensRoute;
